import { GetServerSidePropsContext } from 'next'

import {
  fetchKeystoneConfigurations,
  getRenderTokensConfiguration,
} from '../tools/keystoneFetcher'

import {
  DEVTOOLS_KEYSTONE_DEFAULT_CACHE_TTL_MS,
  DEVTOOLS_KEYSTONE_TOKEN_MS,
  DEVTOOLS_KEYSTONE_TOKEN_SECONDS,
  DEVTOOLS_KEYSTONE_VALIDATION_CACHE_MAX_ENTRIES,
} from './constants'
import { validateTokenExpiration, getTokenExpirationTime } from './tokenValidation'

export interface KeystoneTokenCheckResult {
  isValid: boolean
  isExpired: boolean
  tokenFound: boolean
  error?: string
}

// Cache for Keystone validation results
// Key: token, Value: { isValid: boolean, expiresAt: number }
// Cache expires based on token expiration time or default TTL
interface ValidationCacheEntry {
  isValid: boolean
  expiresAt: number // Timestamp when cache expires
}

const validationCache = new Map<string, ValidationCacheEntry>()

/**
 * Checks if a token exists in Keystone render-tokens configuration
 * Uses caching to avoid calling Keystone on every request
 *
 * @param token - The token to check
 * @param context - Next.js server-side props context
 * @param skipCache - Optional flag to skip cache and force fresh check
 * @returns KeystoneTokenCheckResult with validation status
 */
export async function checkTokenInKeystone(
  token: string,
  context: GetServerSidePropsContext,
  skipCache = false
): Promise<KeystoneTokenCheckResult> {
  const expirationCheck = validateTokenExpiration(token)

  if (expirationCheck.isExpired || !expirationCheck.isValid) {
    return {
      isValid: false,
      isExpired: expirationCheck.isExpired,
      tokenFound: false,
      error:
        expirationCheck.error ||
        (expirationCheck.isExpired ? 'Token has expired' : 'Token validation failed'),
    }
  }

  const trimmedToken = token.trim()

  // Check cache
  if (!skipCache) {
    const cached = validationCache.get(trimmedToken)
    if (cached && Date.now() < cached.expiresAt) {
      return {
        isValid: cached.isValid,
        isExpired: false,
        tokenFound: cached.isValid,
      }
    }
    if (cached) {
      validationCache.delete(trimmedToken)
    }
  }

  // Fetch from Keystone
  const configurations = await fetchKeystoneConfigurations(context)

  if (!configurations || !Array.isArray(configurations)) {
    console.error('prod', 'Keystone API unavailable, DevTools not enabled')
    return {
      isValid: false,
      isExpired: false,
      tokenFound: false,
      error: 'Keystone API unavailable',
    }
  }

  const renderTokensConfig = getRenderTokensConfiguration(configurations)

  if (!renderTokensConfig) {
    console.warn('uat', 'render-tokens configuration not found in Keystone')
    return {
      isValid: false,
      isExpired: false,
      tokenFound: false,
      error: 'render-tokens configuration not found',
    }
  }

  // Check if token exists in render-tokens array
  const tokens = renderTokensConfig.data?.default?.tokens || []
  const tokenFound = tokens.some((t: string) => String(t).trim() === trimmedToken)

  // Cache result with expiration based on token expiration or default TTL
  const tokenExpirationTime = getTokenExpirationTime(trimmedToken)
  const now = Date.now()
  const cacheExpiresAt =
    tokenExpirationTime && tokenExpirationTime > now
      ? tokenExpirationTime -
        DEVTOOLS_KEYSTONE_TOKEN_SECONDS * DEVTOOLS_KEYSTONE_TOKEN_MS
      : now + DEVTOOLS_KEYSTONE_DEFAULT_CACHE_TTL_MS

  validationCache.set(trimmedToken, {
    isValid: tokenFound,
    expiresAt: cacheExpiresAt,
  })

  // Clean up expired cache entries when cache grows too large
  if (validationCache.size > DEVTOOLS_KEYSTONE_VALIDATION_CACHE_MAX_ENTRIES) {
    const currentTime = Date.now()
    for (const [key, value] of validationCache.entries()) {
      if (currentTime >= value.expiresAt) {
        validationCache.delete(key)
      }
    }
  }

  return {
    isValid: tokenFound,
    isExpired: false,
    tokenFound,
  }
}
